import React from 'react';
import { Link } from 'react-router-dom';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthBanner,
  AuthField,
  AuthFields,
  AuthFootnote,
  AuthHint,
  AuthInput,
  AuthPasswordInput,
  AuthSubmit,
} from '../../design-system/patterns/auth';
import { routes } from '../../routes/routes';

/**
 * Admin login step 1, the view. Pure: every input is a prop, every output is a
 * callback. The container (`AdminLogin.jsx`) owns `useAuth`, validation, the
 * toasts, the sessionStorage handoff and the redirects.
 *
 * ADMIN AUTH IS STILL AUTH
 * ------------------------
 * The old screen tried to look administrative: a cyan lightning bolt, a warning
 * emoji, a "Restricted Access" sub-header stacked on top of a second heading
 * that said much the same thing. None of that made the page safer, it only made
 * it a different product. So this is the same AuthShell, the same field stack
 * and the same ink submit as `/login`. What differs is what the brand plate
 * says, which is why `headline` and `proofs` are props on AuthShell: the
 * entrance is described in words rather than in a second visual language.
 *
 * The deterrent copy that was the old footer survives as `AuthHint`. It is
 * worth keeping, it just is not worth a banner.
 *
 * WHY THIS IS CONTROLLED AND `login.view.jsx` IS FORMIK
 * ----------------------------------------------------
 * The exemplar hands Formik its own validation. This page cannot: the container
 * fires `showToast.error("Please fix the errors below")` on a FAILED submit,
 * and Formik has no callback for that, so moving validation into a schema would
 * silently drop a message the app shows today. Values and errors are therefore
 * props, exactly the same purity contract, with the validation left where it
 * already lives.
 */

export function AdminLoginView({
  values = { email: '', password: '' },
  errors = {},
  onChange,
  onSubmit,
  loading = false,
  error,
}) {
  const handleField = (event) => {
    const { name, value } = event.target;
    if (onChange) onChange(name, value);
  };

  return (
    <AuthShell
      title="Admin console"
      subtitle="Administrator credentials only. We email an access code once these check out."
      headline="Administrative access to the Sleevr marketplace."
      proofs={[
        'Two step sign in required',
        'Access code sent by email',
        'Every attempt is logged',
      ]}
      back={{ to: routes.MARKETPLACE, label: 'Return to marketplace' }}
      footer={
        <AuthFootnote>
          Not an administrator? <Link to={routes.LOGIN}>Sign in to your account</Link>
        </AuthFootnote>
      }
    >
      <AuthBanner tone="error">{error}</AuthBanner>

      <form onSubmit={onSubmit} noValidate>
        <AuthFields>
          <AuthField label="Administrator email" error={errors.email}>
            <AuthInput
              name="email"
              type="email"
              value={values.email}
              onChange={handleField}
              placeholder="you@sleevr.co.za"
              autoComplete="username"
              disabled={loading}
            />
          </AuthField>

          <AuthField label="Password" error={errors.password}>
            <AuthPasswordInput
              name="password"
              value={values.password}
              onChange={handleField}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={loading}
            />
          </AuthField>
        </AuthFields>

        <AuthSubmit loading={loading} loadingLabel="Checking credentials">
          Continue
        </AuthSubmit>
      </form>

      <AuthHint>
        This is a restricted administrative interface. Access attempts are logged and
        monitored.
      </AuthHint>
    </AuthShell>
  );
}

export default AdminLoginView;
